import React, { useState } from 'react';
import { uploadAmazonReport, deleteAmazonPeriod } from '../apiAnalytics';
import { useToast } from '../Toast';
import ConfirmDialog from '../ui/ConfirmDialog';
import { FALLBACK_TAG, formatRange, money, untaggedDomains } from '../earnings';
import { relativeTime } from '../domainMetrics';

export const ASSOCIATES_REPORTS_URL = 'https://affiliate-program.amazon.com/p/reporting/earnings';

const REPORT_NAMES = { tracking_id_summary: 'Tracking ID Summary', earnings: 'Earnings (per item)' };

/* Per-tag preview of a parsed report: which site each tracking ID lands on. */
const AttributionTable = ({ byTag, attribution, domainsById }) => {
  const rows = Object.entries(byTag || {}).sort((a, b) => (b[1].earnings || 0) - (a[1].earnings || 0));
  const ownerOf = (tag) => {
    if (tag === FALLBACK_TAG) return <span className="badge badge-warn badge-plain">fallback · unattributed</span>;
    if (attribution.shared && attribution.shared[tag]) {
      return <span className="badge badge-warn badge-plain">shared by {attribution.shared[tag].domains.length} sites</span>;
    }
    if (attribution.unmapped && tag in attribution.unmapped) {
      return <span className="badge badge-danger badge-plain">no site has this tag</span>;
    }
    const owner = Object.values(domainsById || {}).find((d) => String(d.amazon_tag || '').toLowerCase() === tag);
    return owner ? (owner.hostname || owner.id) : <span className="muted">—</span>;
  };
  return (
    <div className="table-wrap" style={{ maxHeight: '260px', overflowY: 'auto' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th>Tracking ID</th>
            <th>Site</th>
            <th className="num">Clicks</th>
            <th className="num">Shipped</th>
            <th className="num">Earnings</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([tag, e]) => (
            <tr key={tag}>
              <td className="mono small">{tag}</td>
              <td className="small">{ownerOf(tag)}</td>
              <td className="num">{e.clicks ? e.clicks.toLocaleString() : '—'}</td>
              <td className="num">{e.items_shipped ? e.items_shipped.toLocaleString() : '—'}</td>
              <td className="num">{money(e.earnings)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* Upload flow: pick file -> preview (dry run, nothing stored) -> save. */
export const AmazonImportModal = ({ onClose, onSaved, domainsById }) => {
  const { addToast } = useToast();
  const [file, setFile] = useState(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (dryRun) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const result = await uploadAmazonReport(file, { periodStart, periodEnd, dryRun });
      if (dryRun) {
        setPreview(result);
      } else {
        addToast(`Amazon report saved: ${formatRange(result.period_start, result.period_end)}, ${money(result.totals.earnings)}`, 'success');
        onSaved && onSaved(result);
        onClose();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.message || 'Upload failed'));
      if (dryRun) setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const att = preview && preview.attribution;
  const unattributed = att
    ? att.fallback + Object.values(att.unmapped).reduce((a, v) => a + v, 0)
      + Object.values(att.shared).reduce((a, s) => a + s.earnings, 0)
    : 0;

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="amazon-import-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); run(!preview); }}
        style={{ maxWidth: '720px' }}
      >
        <div className="modal-header">
          <h3 id="amazon-import-title">Import Amazon Associates report</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body stack">
          <p className="muted small" style={{ margin: 0 }}>
            In <a href={ASSOCIATES_REPORTS_URL} target="_blank" rel="noopener noreferrer">Associates Central → Reports ↗</a>,
            pick the date range, then download the <strong>Tracking ID Summary</strong> report (totals per tracking ID)
            or the <strong>Earnings</strong> report (per item, adds a per-day series). CSV, TSV or XLSX.
            A report for a period that is already stored replaces it.
          </p>
          <div className="field">
            <label htmlFor="amz-file">Report file</label>
            <input
              id="amz-file"
              className="input"
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); setError(''); }}
            />
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="amz-start">Period start (optional)</label>
              <input id="amz-start" className="input" type="date" value={periodStart}
                     onChange={(e) => { setPeriodStart(e.target.value); setPreview(null); }} />
            </div>
            <div className="field">
              <label htmlFor="amz-end">Period end (optional)</label>
              <input id="amz-end" className="input" type="date" value={periodEnd}
                     onChange={(e) => { setPeriodEnd(e.target.value); setPreview(null); }} />
            </div>
          </div>
          <div className="hint muted small" style={{ marginTop: '-8px' }}>
            Leave blank to read the dates from the report&apos;s title line or file name.
          </div>

          {error && <div className="alert alert-error" role="alert"><div className="alert-content">{error}</div></div>}

          {preview && (
            <div className="stack">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '13px' }}>
                <div><div className="kpi-label">Period</div><strong>{formatRange(preview.period_start, preview.period_end)}</strong></div>
                <div><div className="kpi-label">Report</div><strong>{REPORT_NAMES[preview.report_type] || preview.report_type}</strong></div>
                <div><div className="kpi-label">Earnings</div><strong>{money(preview.totals.earnings)}</strong></div>
                <div><div className="kpi-label">Attributed to sites</div><strong>{money(Object.values(att.domains).reduce((a, v) => a + v, 0))}</strong></div>
                <div><div className="kpi-label">Not attributed</div><strong>{money(unattributed)}</strong></div>
              </div>
              {preview.replaces && (
                <div className="alert alert-warning"><div className="alert-content">
                  Replaces the {REPORT_NAMES[preview.replaces.report_type] || 'report'} uploaded
                  {' '}{relativeTime(preview.replaces.uploaded_at) || ''} for the same dates
                  ({money(preview.replaces.earnings)}).
                </div></div>
              )}
              {(preview.warnings || []).map((w) => (
                <div key={w} className="alert alert-info"><div className="alert-content">{w}</div></div>
              ))}
              <AttributionTable byTag={preview.by_tag} attribution={att} domainsById={domainsById} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          {preview ? (
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save report'}</button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={busy || !file}>{busy ? 'Reading…' : 'Preview'}</button>
          )}
        </div>
      </form>
    </div>
  );
};

/*
 * Below the Sites table: what the selected period's Amazon money is made of,
 * which sites have no tracking ID, and the stored reports.
 */
export default function AmazonPanel({ amazon, amazonPeriod, domains, onImport, onChanged, onSelectPeriod }) {
  const { addToast } = useToast();
  const [toDelete, setToDelete] = useState(null);
  const [showUntagged, setShowUntagged] = useState(false);
  const periods = (amazon && amazon.periods) || [];
  const untagged = untaggedDomains(domains);
  const att = amazonPeriod.available ? amazonPeriod.attribution : null;
  const unmapped = att ? Object.entries(att.unmapped) : [];
  const shared = att ? Object.entries(att.shared) : [];

  const remove = async () => {
    const p = toDelete;
    setToDelete(null);
    try {
      await deleteAmazonPeriod(p.period_id);
      addToast(`Removed Amazon report ${formatRange(p.period_start, p.period_end)}`, 'success');
      onChanged && onChanged();
    } catch (err) {
      addToast(err.response?.data?.detail || 'Could not remove the report', 'error');
    }
  };

  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <div className="card-header">
        <h3 className="card-title">Amazon Associates</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a className="btn btn-ghost btn-sm" href={ASSOCIATES_REPORTS_URL} target="_blank" rel="noopener noreferrer">Associates Central ↗</a>
          <button type="button" className="btn btn-primary btn-sm" onClick={onImport}>Import report</button>
        </div>
      </div>

      <div className="stack" style={{ fontSize: '13px' }}>
        <div>
          {amazonPeriod.available ? (
            <>
              <strong>{money(amazonPeriod.total)}</strong> for this period — {amazonPeriod.note}.
              {att && (att.fallback > 0 || unmapped.length > 0 || shared.length > 0) && (
                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                  {att.fallback > 0 && (
                    <li><code>{FALLBACK_TAG}</code> (account fallback, unattributed): {money(att.fallback)}</li>
                  )}
                  {unmapped.map(([tag, v]) => (
                    <li key={tag}><code>{tag}</code>: {money(v)} — no site has this tracking ID; set it on the site to attribute it</li>
                  ))}
                  {shared.map(([tag, s]) => (
                    <li key={tag}><code>{tag}</code>: {money(s.earnings)} — shared by {s.domains.join(', ')}, not split</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <span className="muted">{amazonPeriod.note}.</span>
          )}
        </div>

        <div>
          <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0 }}
                  aria-expanded={showUntagged} onClick={() => setShowUntagged((v) => !v)}>
            {showUntagged ? '▾' : '▸'} {untagged.length} of {(domains || []).length} sites have no tracking ID
          </button>
          <span className="muted"> — their Amazon links fall back to <code>{FALLBACK_TAG}</code>, so their sales cannot be told apart.</span>
          {showUntagged && (
            <div className="muted small" style={{ marginTop: '6px', lineHeight: 1.7 }}>
              {untagged.map((d) => d.hostname || d.name).sort().join(' · ')}
            </div>
          )}
        </div>

        {periods.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Stored report</th>
                  <th>Type</th>
                  <th className="num">Earnings</th>
                  <th>Uploaded</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.period_id}>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" style={{ padding: 0 }}
                              title="Show this period in the table"
                              onClick={() => onSelectPeriod && onSelectPeriod(`amazon:${p.period_id}`)}>
                        {formatRange(p.period_start, p.period_end)}
                      </button>
                    </td>
                    <td className="small">{REPORT_NAMES[p.report_type] || p.report_type}</td>
                    <td className="num">{money(p.totals && p.totals.earnings)}</td>
                    <td className="muted small" title={p.filename || ''}>
                      {relativeTime(p.uploaded_at) || '—'}{p.uploaded_by ? ` by ${p.uploaded_by}` : ''}
                    </td>
                    <td className="actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setToDelete(p)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!toDelete}
        title="Remove this Amazon report?"
        message={toDelete ? `${formatRange(toDelete.period_start, toDelete.period_end)} (${money(toDelete.totals && toDelete.totals.earnings)}) will no longer count toward earnings. You can upload it again.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={remove}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
